/**
 * Google reviews — read from the CMS snapshot, never from Google (owner,
 * 2026-10-02: the Places API (New) bill). The CMS single type `google-reviews`
 * is refreshed by its daily cron (one Place Details call per locale per day,
 * cms/src/api/google-reviews); this module only maps it. fetchAPI's in-process
 * TTL cache collapses the ~100 reads of a build into one. Fails soft: no
 * snapshot → null → the section falls back to the link-only rendering (D21
 * honesty rule — a real figure or none at all). The API key no longer exists
 * on the web side.
 */
import { fetchAPI } from "@lib/strapi/fetch-api";
import { StrapiRoutes } from "@lib/strapi/routes";

export interface GoogleReview {
  author: string;
  authorUrl?: string;
  authorPhoto?: string;
  rating: number;
  text: string;
  relativeTime: string;
}

export interface GoogleReviewsData {
  rating: number;
  count: number;
  reviews: GoogleReview[];
  profileUrl: string;
  writeReviewUrl: string;
}

interface Snapshot {
  placeId?: string;
  rating?: number;
  count?: number;
  fetchedAt?: string;
  reviews?: Record<string, { reviews?: GoogleReview[] }>;
}

/** Reviews the owner does not want on the site (GOOGLE_REVIEWS_HIDE, entries
 *  separated by `|`, matched case-insensitively against the author name or the
 *  review text) — e.g. a review left on the wrong business. Google's aggregate
 *  (rating, count) is untouched: it stays the real figure. The permanent fix is
 *  to flag the review in the Google Business Profile; this is the site-side cut. */
const hiddenReviewMatchers = (): string[] =>
  (process.env.GOOGLE_REVIEWS_HIDE ?? "").split("|").map((s) => s.trim().toLowerCase()).filter(Boolean);
const isHidden = (r: GoogleReview, matchers: string[]): boolean =>
  matchers.some((m) => r.author.toLowerCase().includes(m) || r.text.toLowerCase().includes(m));
const withoutHidden = (data: GoogleReviewsData | null): GoogleReviewsData | null => {
  const matchers = hiddenReviewMatchers();
  if (!data || !matchers.length) return data;
  return { ...data, reviews: data.reviews.filter((r) => !isHidden(r, matchers)) };
};

export const fetchGoogleReviews = async (locale: string): Promise<GoogleReviewsData | null> => {
  try {
    const token = process.env.CMS_API_TOKEN || process.env.STRAPI_API_TOKEN;
    const response = (await fetchAPI(StrapiRoutes.googleReviews(), {}, token)) as { data?: Snapshot | null };
    const snap = response?.data;
    if (!snap || typeof snap.rating !== "number") return null;
    const placeId = snap.placeId || process.env.GOOGLE_PLACE_ID || "";
    // the requested locale's texts, else whatever locale the snapshot holds (GOOGLE_REVIEWS_LOCALES=el)
    const bucket = snap.reviews?.[locale] ?? Object.values(snap.reviews ?? {})[0];
    return withoutHidden({
      rating: snap.rating,
      count: snap.count ?? 0,
      reviews: (bucket?.reviews ?? []).filter((r) => r && r.text),
      profileUrl: `https://search.google.com/local/reviews?placeid=${placeId}`,
      writeReviewUrl: `https://search.google.com/local/writereview?placeid=${placeId}`,
    });
  } catch {
    return null;
  }
};
